import type { i18n as I18nInstance, TFunction } from 'i18next';
import { isApiError } from '@subscriptions/application/errors';

const DEFAULT_FALLBACK_KEY = 'blockCreationWizard.errors.createFailed';

/**
 * Resolves the user-facing message for a raw backend error `code`.
 *
 * Backend codes double as i18n keys under the `subscriptions` namespace — e.g.
 * the code `subscriptionBlock.duplicated` resolves to the translation
 * `subscriptions:subscriptionBlock.duplicated`. When the code is absent or has no
 * translation registered, the provided `fallbackKey` copy is shown instead.
 *
 * Use this overload when the code is already on hand (e.g. carried in route state),
 * so the presentation layer never has to construct an application `ApiError` just to
 * extract it again.
 *
 * @param code - Raw backend error code, or undefined when none is available
 * @param t - i18next translation function bound to the `subscriptions` namespace
 * @param i18n - i18next instance used to probe for key existence
 * @param fallbackKey - i18n key shown when the code has no translation
 * @returns Translated, user-friendly message
 */
export const resolveSubscriptionErrorMessageByCode = (
  code: string | undefined,
  t: TFunction,
  i18n: I18nInstance,
  fallbackKey: string = DEFAULT_FALLBACK_KEY,
): string => {
  if (code && i18n.exists(`subscriptions:${code}`)) {
    return t(code);
  }

  return t(fallbackKey);
};

/**
 * Resolves the user-facing error message for a failed subscription block
 * operation (create, edit or ungroup) from a caught error.
 *
 * Narrows the caught value to an `ApiError` and delegates to
 * {@link resolveSubscriptionErrorMessageByCode} with its `code`. The raw
 * `error.message` is never surfaced because the backend emits it in English and
 * would leak to Spanish-speaking users.
 *
 * @param error - The error caught from the block mutation
 * @param t - i18next translation function bound to the `subscriptions` namespace
 * @param i18n - i18next instance used to probe for key existence
 * @param fallbackKey - i18n key shown when the error code has no translation
 * @returns Translated, user-friendly message ready to feed into a toast
 */
export const resolveSubscriptionErrorMessage = (
  error: unknown,
  t: TFunction,
  i18n: I18nInstance,
  fallbackKey: string = DEFAULT_FALLBACK_KEY,
): string => resolveSubscriptionErrorMessageByCode(isApiError(error) ? error.code : undefined, t, i18n, fallbackKey);
